import clsx from 'clsx';
import { HTMLAttributes, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CheckCircleIcon, CopyAllIcon, KeyboardArrowDownIcon } from 'icons';
import { Button, Collapse, Fade, Grid, IconButton, styled } from 'netline-ui';
import { ErrorItemBase } from 'types';

import { JsonViewer } from '../JsonViewer';

import styles, { errorDetailsClasses, errorDetailsRootClasses } from './ErrorDetails.styles';

const ErrorDetailsRoot = styled('div', { name: errorDetailsClasses.$name })(styles);

type ErrorDetailsProps = HTMLAttributes<HTMLDivElement> & { details: ErrorItemBase['details'] };

const ErrorDetails = ({ className, details, ...props }: ErrorDetailsProps) => {
  const { t } = useTranslation();
  const [detailsExpanded, setDetailsExpanded] = useState(false);

  const handleExpandDetails = useCallback(() => {
    setDetailsExpanded((prevExpanded) => !prevExpanded);
  }, []);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const [copied, setCopied] = useState(false);
  const handleCopyToClipboard = useCallback(() => {
    if (!inputRef.current) {
      return;
    }
    window.navigator.clipboard.writeText(inputRef.current.value);
    setCopied(true);
  }, []);

  return (
    <ErrorDetailsRoot
      className={clsx(className, errorDetailsClasses.root, {
        [errorDetailsRootClasses.expanded]: detailsExpanded,
      })}
      data-testid="ErrorDetails-root"
      {...props}
    >
      <div className={errorDetailsClasses.title}>
        <span>{t('error_view.details')}</span>
        <IconButton
          className={errorDetailsClasses.expandButton}
          data-testid="ErrorDetails-expandButton"
          size="small"
          onClick={handleExpandDetails}
        >
          <KeyboardArrowDownIcon />
        </IconButton>
      </div>
      <div className={errorDetailsClasses.content} data-testid="ErrorDetails-content">
        <Collapse in={detailsExpanded}>
          <JsonViewer json={details} data-testid="ErrorDetails-json" />
          <Grid container spacing={1} alignItems="center" className={errorDetailsClasses.footer}>
            <Grid item xs={false}>
              <Button
                onClick={handleCopyToClipboard}
                data-testid="ErrorDetails-clipboardButton"
                startIcon={<CopyAllIcon />}
              >
                <input ref={inputRef} id="ErrorDetails-data" type="hidden" value={JSON.stringify(details)} />
                {t('error_view.copy_details')}
              </Button>
            </Grid>
            <Grid item xs={false}>
              <Fade in={copied}>
                <CheckCircleIcon fontSize="small" className={errorDetailsClasses.copiedIcon} />
              </Fade>
            </Grid>
            <Grid item xs={false}>
              <Fade in={copied}>
                <span>{t('error_view.details_copied')}</span>
              </Fade>
            </Grid>
          </Grid>
        </Collapse>
      </div>
    </ErrorDetailsRoot>
  );
};
export default ErrorDetails;
